import {
  Checkbox,
  FormControlLabel,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import { GoogleLoginButton } from "react-social-login-buttons";
import { Link as RouterLink } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useGoogleSignInMutation } from "src/redux/service/login.service";
import { setToken } from "src/redux/reducers/user";
import theme, { backgroundColor } from "src/styles/mainstyle";
import { useEffect, useRef, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import OnboardingUserPageModal from "./OnboardingUserPageModal";
import OnboardingHomePageModal from "./OnboardingHomePageModal";
import { trackPostHogEvent } from "src/analytics/posthog";

type SignupModalProps = {
  additionalText?: boolean;
  open: boolean;
  onClose: () => void;
};

type SelfUserOnboardingState = {
  phoneNumber?: string;
  questions?: Record<string, string>;
};

type OwnHomeOnboardingState = {
  _id?: string;
  descriptions?: Record<string, string>;
};

const backendURL =
  window?.env.REACT_APP_BACKEND_URL || "http://localhost:3003/";

// Manual test switch: set to true to force onboarding modals on any account.
const FORCE_ONBOARDING_FOR_TESTING = false;

const SignupModal = ({ open, onClose, additionalText }: SignupModalProps) => {
  const [googleSignIn] = useGoogleSignInMutation();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [openUserPagePopupStep, setOpenUserPagePopupStep] = useState(false);
  const [openHomePagePopupStep, setOpenHomePagePopupStep] = useState(false);
  const [shouldOpenHomePageStep, setShouldOpenHomePageStep] = useState(false);
  const mobileTheme = useTheme();
  const isDesktop = useMediaQuery(mobileTheme.breakpoints.down("sm"));
  const previousOpenRef = useRef(false);

  useEffect(() => {
    if (open && !previousOpenRef.current) {
      trackPostHogEvent("signup_modal_opened", {
        source: additionalText ? "gated_action" : "main_signup",
      });
    }

    previousOpenRef.current = open;
  }, [open, additionalText]);

  const getOnboardingNeedsFromBackend = async (token: string) => {
    const headers = { Authorization: token };

    const userResult = await axios.get<SelfUserOnboardingState>(
      `${backendURL}user`,
      { headers },
    );

    const userNeedsOnboarding =
      !userResult.data?.phoneNumber ||
      !userResult.data?.questions?.shortDescription;
    const hasPhoneNumber = Boolean(userResult.data?.phoneNumber);
    const hasShortDescription = Boolean(userResult.data?.questions?.shortDescription);

    let homeNeedsOnboarding = false;
    let hasHome = false;
    try {
      const homeResult = await axios.get<OwnHomeOnboardingState>(
        `${backendURL}home`,
        { headers },
      );
      hasHome = Boolean(homeResult.data?._id || homeResult.data?.descriptions?.title);
      homeNeedsOnboarding = !homeResult.data?.descriptions?.title;
    } catch {
      hasHome = false;
      homeNeedsOnboarding = true;
    }

    return {
      userNeedsOnboarding,
      homeNeedsOnboarding,
      hasHome,
      hasPhoneNumber,
      hasShortDescription,
    };
  };

  const handleUserPagePopupStep = () => {
    setOpenUserPagePopupStep(false);
    if (shouldOpenHomePageStep) {
      setOpenHomePagePopupStep(true);
    }
  };

  const handleHomePagePopupStep = () => {
    setOpenHomePagePopupStep(false);
  };

  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const res = await googleSignIn(codeResponse);
      if ("data" in res) {
        const token = res.data?.token;
        setToken(token);
        trackPostHogEvent("auth_success", {
          source: "google_signup",
        });

        try {
          const {
            userNeedsOnboarding,
            homeNeedsOnboarding,
            hasHome,
            hasPhoneNumber,
            hasShortDescription,
          } =
            await getOnboardingNeedsFromBackend(token);
          const effectiveUserNeedsOnboarding =
            FORCE_ONBOARDING_FOR_TESTING || userNeedsOnboarding;
          const effectiveHomeNeedsOnboarding =
            FORCE_ONBOARDING_FOR_TESTING || homeNeedsOnboarding;

          trackPostHogEvent("onboarding_state_checked", {
            has_home: hasHome,
            needs_user_onboarding: effectiveUserNeedsOnboarding,
            needs_home_onboarding: effectiveHomeNeedsOnboarding,
            has_phone_number: hasPhoneNumber,
            has_short_description: hasShortDescription,
          });

          setShouldOpenHomePageStep(effectiveHomeNeedsOnboarding);

          if (effectiveUserNeedsOnboarding) {
            setOpenUserPagePopupStep(true);
          } else if (effectiveHomeNeedsOnboarding) {
            setOpenHomePagePopupStep(true);
          }
        } catch (error) {
          console.error("Failed to load onboarding state:", error);
          trackPostHogEvent("onboarding_state_checked", {
            has_home: false,
            needs_user_onboarding: true,
            needs_home_onboarding: true,
            has_phone_number: false,
            has_short_description: false,
            source: "onboarding_check_failed",
          });
          if (FORCE_ONBOARDING_FOR_TESTING) {
            setShouldOpenHomePageStep(true);
          }
          setOpenUserPagePopupStep(true);
        }

        onClose();
      } else if ("error" in res) {
        console.log("Error:", res.error);
      }
    },
    onError: (error) => console.log("Login Failed:", error),
  });
  return (
    <ThemeProvider theme={theme}>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: !isDesktop ? "450px" : "90vw",
            maxWidth: "500px",
            bgcolor: "white",
            borderRadius: "16px",
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 4,
              py: 3,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Welcome to Home Swap
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                color: "#666",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ px: 4, py: 4 }}>
            {additionalText && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  bgcolor: "#fff3e0",
                  border: "1px solid #ffb74d",
                  borderRadius: "8px",
                  px: 2,
                  py: 2,
                  mb: 3,
                }}
              >
                <InfoOutlinedIcon
                  sx={{ color: "#f57c00", mr: 1.5, mt: 0.2, fontSize: "20px" }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#424242", lineHeight: 1.6 }}
                >
                  You need to sign up to browse homes and connect with other
                  travelers.
                </Typography>
              </Box>
            )}

            <Typography
              variant="h6"
              sx={{
                mb: 1,
                color: "#333",
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              Sign in to Home Swap
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 3,
                color: "#666",
                textAlign: "center",
              }}
            >
              Connect with people worldwide to swap homes
            </Typography>

            {/* Move checkbox ABOVE button */}
            <Box
              sx={{
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 2,
                mb: 3,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => {
                      const isAccepted = e.target.checked;
                      setAcceptedTerms(isAccepted);
                      if (isAccepted) {
                        trackPostHogEvent("terms_accepted", {
                          source: "signup_modal",
                        });
                      }
                    }}
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": {
                        color: backgroundColor,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    I agree to the{" "}
                    <RouterLink
                      to="/terms-and-condition"
                      style={{
                        color: backgroundColor,
                        textDecoration: "underline",
                        fontWeight: 500,
                      }}
                    >
                      Terms and Conditions
                    </RouterLink>{" "}
                    <span style={{ color: "#f44336" }}>*</span>
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>

            <GoogleLoginButton
              disabled={!acceptedTerms}
              onClick={() => {
                trackPostHogEvent("google_signup_started", {
                  source: "signup_modal",
                });
                login();
              }}
              text="Continue with Google"
              style={{
                fontSize: "16px",
                borderRadius: "8px",
                filter: acceptedTerms ? "none" : "grayscale(100%)",
                opacity: acceptedTerms ? 1 : 0.6,
                cursor: acceptedTerms ? "pointer" : "not-allowed",
                boxShadow: acceptedTerms
                  ? "0 2px 8px rgba(0,0,0,0.15)"
                  : "none",
                transition: "all 0.2s ease",
              }}
            />
          </Box>

          {/* Keep your original footer */}
          <Box
            sx={{
              px: 4,
              py: 2,
              bgcolor: "#f9f9f9",
              borderTop: "1px solid #e0e0e0",
              textAlign: "center",
            }}
          >
            <Typography variant="caption" sx={{ color: "#999" }}>
              By continuing, you can browse homes, connect with others, and
              start your home swap journey.
            </Typography>
          </Box>
        </Box>
      </Modal>

      <OnboardingUserPageModal
        open={openUserPagePopupStep}
        onClose={() => setOpenUserPagePopupStep(false)}
        onConfirm={handleUserPagePopupStep}
      />

      <OnboardingHomePageModal
        open={openHomePagePopupStep}
        onClose={() => setOpenHomePagePopupStep(false)}
        onConfirm={handleHomePagePopupStep}
      />
    </ThemeProvider>
  );
};

export default SignupModal;
